import { FC, useContext, useEffect, useState } from "react";
import SimpleCard from "../simple-card";
import ClinicsDropdown from "./clinics-dropdown";
import { Button, Col, Row } from "react-bootstrap";
import FlexBox from "../flex-box";
import ClinicPreview from "lib/components/clinics/clinic-preview";
import useClinics from "lib/hooks/useClinics";
import { getArrayOfValuesFromArray, mergeArrays } from "lib/utils/array-utils";
import ClinicMonthlyDataChart from "../charts/clinic-monthly-data-chart";
import ClinicRevenueChart from "../charts/clinic-revenue-chart";
import ClinicBookingsChart from "../charts/clinic-bookings-chart";
import RoomOccupancyChart from "../charts/room-occupancy-chart";
import { PiArrowClockwiseLight } from "react-icons/pi";
import { AppContext } from "lib/contexts/app-context";
import { isSuperAdminUser } from "lib/utils/util";
import useAnalytics from "lib/hooks/useAnalytics";

interface ClinicsPanelProps {
  onClinicChange?: (clinic: Clinic) => void;
  isLoading?: boolean;
  error?: string;
  children?: React.ReactNode;
}

const ClinicsPanel: FC<ClinicsPanelProps> = ({
  onClinicChange,
  children,
  isLoading,
  error,
}) => {
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [bookingsEntry, setBookingsEntry] = useState<BookingEntry[]>([]);
  const [revenueEntry, setRevenueEntry] = useState<RevenueEntry[]>([]);
  const [subscriptionEntry, setSubscriptionEntry] = useState<
    SubscriptionEntry[]
  >([]);
  const [occupancyEntry, setOccupancyEntry] = useState<RoomOccupancyEntry[]>(
    []
  );
  const [bookingsLoadingCombined, setBookingsLoadingCombined] =
    useState<boolean>(false);
  const [subscriptionsLoading, setSubscriptionsLoading] =
    useState<boolean>(false);
  const [bookingsLoadingOnly, setBookingsLoadingOnly] =
    useState<boolean>(false);

  const {
    clinics,
    calenderDetails,
    getClinics,
    getCalenderDetails,
    getClinicProfilePic,
    loading: clinicsLoading,
    error: clinicsError,
  } = useClinics();

  const {
    getClinicBookingsGraphData,
    getClinicRevenueGraphData,
    getClinicSubscriptionsGraphData,
    getClinicOccupancyGraphData,
    clinicRevenueGraphLoading,
    clinicOccupancyGraphLoading,
    error: analyticsError,
  } = useAnalytics();

  const { appState } = useContext(AppContext);

  const isSuperAdmin = isSuperAdminUser(appState?.userRole);
  const [clinicProfilePic, setClinicProfilePic] = useState<string | null>(null);

  const handleClinicsLoaded = (clinics: Clinic[]) => {
    if (clinics?.length > 0) {
      setSelectedClinic(clinics[0]);
      getCalenderDetails(clinics[0].id);
      getClinicProfilePic(clinics[0].id, (profilePic) => {
        setClinicProfilePic(profilePic);
      });
      onClinicChange?.(clinics[0]);
    }
  };
  const getClinicBookingsGraphDataWithLoader = async (
    clinicId: string,
    setData: (data: any) => void,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true);
    try {
      await getClinicBookingsGraphData(clinicId, setData);
    } finally {
      setLoading(false);
    }
  };

  const getClinicSubscriptionsGraphDataWithLoader = async (
    clinicId: string,
    setData: (data: any) => void,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true);
    try {
      await getClinicSubscriptionsGraphData(clinicId, setData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getClinics(isSuperAdmin, handleClinicsLoaded);
  }, []);

  useEffect(() => {
    if (selectedClinic) {
      getClinicBookingsGraphData(selectedClinic?.id ?? "", setBookingsEntry);
      getClinicRevenueGraphData(selectedClinic?.id ?? "", setRevenueEntry);
      getClinicOccupancyGraphData(selectedClinic?.id ?? "", setOccupancyEntry);
      getClinicSubscriptionsGraphData(
        selectedClinic?.id ?? "",
        setSubscriptionEntry
      );
      getCalenderDetails(selectedClinic?.id)
    }
  }, [selectedClinic]);
  /** Calculated Vars */
  const unavailableDays = mergeArrays<UnavailableDay>(
    calenderDetails?.[0]?.unavailableDates ?? [],
    calenderDetails?.[0]?.holidays ?? []
  );

  /** Calculated Vars */

  return (
    <SimpleCard
      isLoading={clinicsLoading || isLoading}
      error={clinicsError || error || analyticsError}
      title="Clinics"
      action={
        <ClinicsDropdown
          clinics={clinics ?? []}
          onSelect={(clinic) => {
            setSelectedClinic(clinic);
            getClinicProfilePic(clinic?.id, (profilePic) => {
              setClinicProfilePic(profilePic);
            });
            onClinicChange?.(clinic);
          }}
          selectedClinic={selectedClinic}
        />
      }
    >
      {children ?? (
        <Row>
          <Col sm="7">
            <FlexBox
              justifyContent="start"
              alignItems="start"
              direction="column"
              className="w-100 gap-2"
            >
              <SimpleCard
                className="w-100"
                isLoading={bookingsLoadingCombined || subscriptionsLoading}
              >
                <FlexBox
                  className="border-bottom"
                  justifyContent="between"
                  alignItems="center"
                >
                  <h5>Bookings & Subscriptions</h5>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      getClinicBookingsGraphDataWithLoader(
                        selectedClinic?.id ?? "",
                        setBookingsEntry,
                        setBookingsLoadingCombined
                      );
                      getClinicSubscriptionsGraphDataWithLoader(
                        selectedClinic?.id ?? "",
                        setSubscriptionEntry,
                        setSubscriptionsLoading
                      );
                    }}
                  >
                    <PiArrowClockwiseLight />
                  </Button>
                </FlexBox>
                <ClinicBookingsChart
                  bookingData={bookingsEntry}
                  subscriptionsData={subscriptionEntry}
                  height={300}
                />
              </SimpleCard>

              <SimpleCard className="w-100" isLoading={bookingsLoadingOnly}>
                <FlexBox
                  className="border-bottom"
                  justifyContent="between"
                  alignItems="center"
                >
                  <h5>Clinic Bookings</h5>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      getClinicBookingsGraphDataWithLoader(
                        selectedClinic?.id ?? "",
                        setBookingsEntry,
                        setBookingsLoadingOnly
                      );
                    }}
                  >
                    <PiArrowClockwiseLight />
                  </Button>
                </FlexBox>
                <ClinicMonthlyDataChart data={bookingsEntry} height={300} />
              </SimpleCard>

              <SimpleCard
                className="w-100"
                isLoading={clinicRevenueGraphLoading}
              >
                <FlexBox
                  className="border-bottom"
                  justifyContent="between"
                  alignItems="center"
                >
                  <h5>Clinic Revenue</h5>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      getClinicRevenueGraphData(
                        selectedClinic?.id ?? "",
                        setRevenueEntry
                      );
                    }}
                  >
                    <PiArrowClockwiseLight />
                  </Button>
                </FlexBox>
                <ClinicRevenueChart data={revenueEntry} height={300} />
              </SimpleCard>
            </FlexBox>
          </Col>
          <Col sm="5">
            <Row className="gap-3">
              <Col xs="12">
                <ClinicPreview
                  clinicProfilePicUrl={clinicProfilePic}
                  clinic={selectedClinic}
                  clinicCalendar={calenderDetails}
                  unavailableDays={unavailableDays}
                  holidays={getArrayOfValuesFromArray(
                    calenderDetails?.holidays ?? [],
                    "internalDate"
                  )}
                  clinicWorkingHours={selectedClinic?.availableHours}
                  bookedDays={[]}
                />
              </Col>
              <Col xs="12">
                <SimpleCard isLoading={clinicOccupancyGraphLoading}>
                  <FlexBox
                    className="border-bottom"
                    justifyContent="between"
                    alignItems="center"
                  >
                    <h5>Clinic Occupancy</h5>
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        getClinicOccupancyGraphData(
                          selectedClinic?.id ?? "",
                          setOccupancyEntry
                        );
                      }}
                    >
                      <PiArrowClockwiseLight />
                    </Button>
                  </FlexBox>
                  <RoomOccupancyChart
                    roomDetails={occupancyEntry}
                    height={200}
                  />
                </SimpleCard>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </SimpleCard>
  );
};
export default ClinicsPanel;
